import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import type { WorkflowDefinition, WorkflowNode } from '@corner/shared';
import { APP } from '../tokens';
import {
  useWorkflowRunStream,
  type WorkflowRunStep,
} from '../../../hooks/useWorkflowRunStream';
import ContractRunWizard, { type WorkflowTemplateSummary } from './ContractRunWizard';
import type { SaveAsWorkflowMode } from './SaveAsWorkflowPrompt';
import {
  createWorkflowTemplate,
  listWorkflowTemplates,
  saveContractSendAsWorkflow,
} from '../../../api/workflowApi';
import { toast } from '../../../hooks/use-toast';
import { buildWorkflowThreadSummary } from '../../../lib/buildWorkflowThreadSummary';
import type { WorkflowThreadSummary } from '../../../types';

// ─── Types ─────────────────────────────────────────────────────────────────

/**
 * Two run-panel layouts share this entry point:
 * - `'standard'` — the existing step-flow visualisation used for analysis /
 *   extraction workflows where the user wants to inspect every node.
 * - `'wizard'` — contract send: **Recipients** as a centered modal, then
 *   **Document** and **Preview & send** as full-width wizard screens; followed by a 4-stage progress strip.
 *
 * Default `'auto'` resolves to `'wizard'` when the graph contains a
 * `dispatch_for_signature` node and we have everything the wizard needs
 * (definition + workflowId + onRun handler), otherwise `'standard'`.
 *
 * `'hud'` is kept as a deprecated alias of `'wizard'` for callers that
 * still pass it after the prior single-form HUD was replaced.
 */
export type WorkflowRunPanelViewMode = 'auto' | 'standard' | 'wizard' | 'hud';

export interface WorkflowRunPanelProps {
  runId: string | null;
  getToken: () => string | null;
  onClose: () => void;
  onRunAgain?: () => void;
  onGoBack?: () => void;
  viewMode?: WorkflowRunPanelViewMode;
  // HUD-only props ─ ignored by the standard view.
  definition?: WorkflowDefinition;
  workflowId?: string;
  onRun?: (params: {
    workflowId: string;
    initialVariables: Record<string, unknown>;
    documentIds: string[];
  }) => void | Promise<void>;

  // ── Template memory: source-picker templates section + save prompt ─
  /** Saved templates for the parent workflow. */
  templates?: WorkflowTemplateSummary[];
  /** Which save prompt to show after a successful run. Omit to hide. */
  saveAsMode?: SaveAsWorkflowMode;
  saveAsWorkflowName?: string;
  saveAsDefaultName?: string;
  onSaveAsWorkflow?: (input: {
    name: string;
    mode: SaveAsWorkflowMode;
    signingRequestId?: string;
    sourceFileId?: string;
  }) => void | Promise<void>;
  /** Posted once when a run reaches `completed` — parent typically appends a chat message. */
  onAppendThreadSummary?: (summary: WorkflowThreadSummary) => void;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const NODE_TYPE_LABEL: Record<string, string> = {
  document_input: 'Input',
  review_task: 'Review',
  extraction_task: 'Extract',
  classify_task: 'Classify',
  drafting_task: 'Draft',
  condition: 'Condition',
  approval_gate: 'Approval',
  export_action: 'Export',
};

function formatDuration(startISO: string | null, endISO: string | null): string {
  if (!startISO) return '—';
  const start = new Date(startISO).getTime();
  const end = endISO ? new Date(endISO).getTime() : Date.now();
  const ms = Math.max(0, end - start);
  if (ms < 1000) return `${ms}ms`;
  const s = (ms / 1000).toFixed(1);
  return `${s}s`;
}

function formatTime(isoStr: string | null): string {
  if (!isoStr) return '—';
  return new Date(isoStr).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** `App.tsx` uses `preset:${definition.workflowId}` for built-ins — not a persisted UserWorkflow ObjectId. */
function isSyntheticPresetWorkflowId(workflowId: string): boolean {
  return workflowId.startsWith('preset:');
}

// ─── Component ─────────────────────────────────────────────────────────────

export default function WorkflowRunPanel({
  runId,
  getToken,
  onClose,
  onRunAgain,
  onGoBack,
  viewMode = 'auto',
  definition,
  workflowId,
  onRun,
  templates,
  saveAsMode,
  saveAsWorkflowName,
  saveAsDefaultName,
  onSaveAsWorkflow,
  onAppendThreadSummary,
}: WorkflowRunPanelProps) {
  // Auto-pick the wizard layout when the workflow is a contract velocity flow
  // (has a dispatch node) AND the caller passed everything we need.
  const resolvedView: 'standard' | 'wizard' = (() => {
    if (viewMode === 'standard') return 'standard';
    if (viewMode === 'wizard' || viewMode === 'hud') return 'wizard';
    const hasDispatch = definition?.graph?.nodes?.some((n) => n.type === 'dispatch_for_signature');
    if (hasDispatch && definition && workflowId && onRun) return 'wizard';
    return 'standard';
  })();

  // Auto-fetch templates for this workflow when the caller didn't pass
  // them explicitly. Empty array = "fetched, no templates yet" so the
  // wizard hides the section. Re-fetches when workflowId changes.
  // Deliberately fires only in wizard view to avoid unnecessary work
  // for standard-view callers.
  const [fetchedTemplates, setFetchedTemplates] = useState<WorkflowTemplateSummary[] | undefined>(undefined);
  useEffect(() => {
    if (templates !== undefined) return; // caller-overridden — skip fetch
    if (resolvedView !== 'wizard' || !workflowId) return;
    if (isSyntheticPresetWorkflowId(workflowId)) {
      setFetchedTemplates([]);
      return;
    }
    let cancelled = false;
    listWorkflowTemplates(workflowId)
      .then((list) => {
        if (cancelled) return;
        setFetchedTemplates(list.map((t) => ({
          templateId: t.id,
          name: t.name,
          sourceFileId: t.sourceFileId,
          lastUsedAt: t.lastUsedAt ?? undefined,
          useCount: t.useCount,
        })));
      })
      .catch((err) => {
        // Non-fatal: the wizard simply renders without the Saved
        // Templates section, falling back to the existing pickers.
        console.warn('[WorkflowRunPanel] template fetch failed', err);
        if (!cancelled) setFetchedTemplates([]);
      });
    return () => { cancelled = true; };
  }, [templates, resolvedView, workflowId]);

  const effectiveTemplates = templates ?? fetchedTemplates;

  // Default save handler — wired to backend when the caller didn't
  // pass an explicit onSaveAsWorkflow override. Two paths:
  //   mode='workflow' (user is saving for the first time) →
  //     saveContractSendAsWorkflow creates a UserWorkflow from the
  //     completed signing request, then we create a TemplateDocument
  //     under that new workflow with the source file as the master.
  //   mode='template' (workflow already exists) →
  //     createWorkflowTemplate against the current workflowId.
  // Both paths surface a toast on success/failure.
  const defaultSaveHandler = useMemo(() => {
    return async (input: {
      name: string;
      mode: SaveAsWorkflowMode;
      signingRequestId?: string;
      sourceFileId?: string;
    }) => {
      try {
        let targetWorkflowId = workflowId;
        if (input.mode === 'workflow') {
          if (!input.signingRequestId) {
            throw new Error('Cannot save: signingRequestId not available');
          }
          const created = await saveContractSendAsWorkflow({
            signingRequestId: input.signingRequestId,
            name: input.name,
          });
          targetWorkflowId = created.id;
        }
        if (!targetWorkflowId) {
          throw new Error('Cannot save: workflowId not available');
        }
        if (input.sourceFileId) {
          // Best-effort template creation. The workflow save already
          // succeeded, so a template-create failure shouldn't block
          // the toast — we still confirm the workflow saved, with a
          // subtle "couldn't link a template" hint.
          await createWorkflowTemplate(targetWorkflowId, {
            name: input.name,
            sourceFileId: input.sourceFileId,
          }).catch((err) => {
            console.warn('[WorkflowRunPanel] template create failed', err);
          });
        }
        toast({
          title: input.mode === 'workflow' ? 'Workflow saved' : 'Template saved',
          description: input.mode === 'workflow'
            ? `"${input.name}" is in your Workflows panel — open it to send a similar contract in seconds.`
            : `"${input.name}" is now a template under this workflow.`,
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Save failed';
        toast({
          title: input.mode === 'workflow' ? 'Couldn\'t save workflow' : 'Couldn\'t save template',
          description: message,
          variant: 'destructive',
        });
        throw err; // let the wizard's local optimistic UI revert
      }
    };
  }, [workflowId]);

  const effectiveSaveHandler = onSaveAsWorkflow ?? defaultSaveHandler;

  if (resolvedView === 'wizard') {
    return (
      <ContractRunWizard
        definition={definition!}
        workflowId={workflowId!}
        runId={runId}
        getToken={getToken}
        onRun={onRun!}
        onClose={onClose}
        onRunAgain={onRunAgain}
        onGoBack={onGoBack}
        templates={effectiveTemplates}
        // Default to 'template' — the most common case is a returning
        // user adding a new doc to an established workflow. Callers
        // can override (e.g. App.tsx's first-run-from-chat path
        // passes 'workflow').
        saveAsMode={saveAsMode ?? 'template'}
        saveAsWorkflowName={saveAsWorkflowName ?? definition?.label}
        saveAsDefaultName={saveAsDefaultName}
        onSaveAsWorkflow={effectiveSaveHandler}
        onAppendThreadSummary={onAppendThreadSummary}
      />
    );
  }

  // The standard view requires a runId; if missing, render a placeholder.
  if (!runId) {
    return (
      <div style={CONTAINER}>
        <div style={EMPTY}>
          <span>No run yet — start the workflow to see progress here.</span>
        </div>
      </div>
    );
  }

  return (
    <StandardRunPanel
      runId={runId}
      getToken={getToken}
      onClose={onClose}
      onRunAgain={onRunAgain}
      onGoBack={onGoBack}
      onAppendThreadSummary={onAppendThreadSummary}
    />
  );
}

function StandardRunPanel({
  runId,
  getToken,
  onClose,
  onRunAgain,
  onGoBack,
  onAppendThreadSummary,
}: {
  runId: string;
  getToken: () => string | null;
  onClose: () => void;
  onRunAgain?: () => void;
  onGoBack?: () => void;
  onAppendThreadSummary?: (summary: WorkflowThreadSummary) => void;
}) {
  const { run, cancel } = useWorkflowRunStream({ runId, getToken });
  const chatPostedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onAppendThreadSummary || !run || run.status !== 'completed') return;
    if (chatPostedRef.current === run.id) return;
    chatPostedRef.current = run.id;
    onAppendThreadSummary(buildWorkflowThreadSummary(run));
  }, [run, onAppendThreadSummary]);

  const nodesById = useMemo(() => {
    const map = new Map<string, WorkflowNode>();
    run?.definition.graph.nodes.forEach((n) => map.set(n.nodeId, n));
    return map;
  }, [run]);

  const orderedSteps = useMemo(() => orderSteps(run?.steps ?? [], run?.definition), [run]);

  const progress = useMemo(() => {
    if (!orderedSteps.length) return 0;
    const done = orderedSteps.filter((s) => s.status === 'completed' || s.status === 'skipped').length;
    return Math.round((done / orderedSteps.length) * 100);
  }, [orderedSteps]);

  const isTerminal = run?.status === 'completed' || run?.status === 'failed' || run?.status === 'canceled';

  const completedCount = orderedSteps.filter((s) => s.status === 'completed').length;
  const totalElapsed = run?.startedAt ? formatDuration(run.startedAt, run.completedAt ?? null) : '—';

  return (
    <div style={CONTAINER}>
      {/* ─── Top Bar ───────────────────────────────── */}
      <div style={TOP_BAR}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <button type="button" onClick={onClose} style={BREADCRUMB_BTN} aria-label="Back to workflows">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L5 7L9 12" stroke={APP.textSecondary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={BREADCRUMB_TEXT}>Workflows</span>
          </button>
          <div style={DIVIDER_V} />
          <span style={WORKFLOW_NAME_STYLE}>{run?.definition?.label ?? 'Workflow'}</span>
          {run?.status === 'running' && (
            <div style={STATUS_BADGE}>
              <div style={{ ...STATUS_DOT, backgroundColor: '#D97645' }} />
              <span style={{ ...STATUS_BADGE_TEXT, color: '#D97645' }}>Running...</span>
            </div>
          )}
          {run?.status === 'completed' && (
            <div style={STATUS_BADGE}>
              <div style={{ ...STATUS_DOT, backgroundColor: 'rgba(27,122,75,0.12)' }} />
              <span style={{ ...STATUS_BADGE_TEXT, color: '#1B7A4B' }}>Complete</span>
            </div>
          )}
          {run?.status === 'failed' && (
            <div style={STATUS_BADGE}>
              <div style={{ ...STATUS_DOT, backgroundColor: '#C4622A' }} />
              <span style={{ ...STATUS_BADGE_TEXT, color: '#C4622A' }}>Failed</span>
            </div>
          )}
        </div>
        {!isTerminal && run && (
          <button type="button" onClick={() => cancel()} style={TOPBAR_BTN}>Cancel</button>
        )}
        {isTerminal && onRunAgain && (
          <button type="button" onClick={onRunAgain} style={TOPBAR_BTN}>Run Again</button>
        )}
        {/* Progress bar */}
        {run && !isTerminal && (
          <div style={PROGRESS_TRACK}>
            <div style={{ ...PROGRESS_FILL, width: `${progress}%` }} />
          </div>
        )}
      </div>

      {/* ─── Main Content ──────────────────────────── */}
      <div style={MAIN_CONTENT}>
        {/* ─── Step Flow Canvas ───────────────────── */}
        <div style={STEP_FLOW}>
          {!run && (
            <div style={EMPTY}><span>Loading run…</span></div>
          )}

          {run && orderedSteps.map((step, i) => {
            const node = nodesById.get(step.nodeId);
            const hasTerminalCard = run.status === 'completed' || run.status === 'failed';
            const isLast = i === orderedSteps.length - 1 && !hasTerminalCard;
            const statusColor = stepStatusColor(step.status);
            const eyebrowLabel = step.status === 'completed'
              ? (node?.type === 'document_input' ? 'Input' : 'Complete')
              : step.status === 'running' ? 'Running' : step.status === 'failed' ? 'Failed' : (NODE_TYPE_LABEL[node?.type ?? ''] ?? 'Pending');
            const timeStr = step.status === 'running' ? '' : formatDuration(step.startedAt, step.completedAt);

            return (
              <div key={step.nodeId} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 460 }}>
                {/* Card */}
                <div style={stepCardStyle(step.status, node?.type)}>
                  <StepIcon status={step.status} />
                  <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ ...STEP_EYEBROW_STYLE, color: statusColor }}>{eyebrowLabel}</span>
                      {timeStr && <span style={STEP_TIME}>{timeStr}</span>}
                    </div>
                    <span style={STEP_TITLE}>{node?.label ?? step.nodeId}</span>
                    {typeof step.output === 'string' && step.output ? (
                      <span style={STEP_DESC}>{step.output}</span>
                    ) : null}
                    {step.error && <span style={STEP_ERR}>{step.error}</span>}
                  </div>
                </div>
                {/* Connector */}
                {!isLast && (
                  <div style={{ width: 2, height: 24, backgroundColor: step.status === 'completed' && node?.type === 'document_input' ? '#C8E6C9' : '#E8DED4', flexShrink: 0 }} />
                )}
              </div>
            );
          })}

          {/* ─── Success Card ──────────────────────── */}
          {run && run.status === 'completed' && (
            <div style={COMPLETE_CARD}>
              <div style={COMPLETE_HEADER}>
                <div style={COMPLETE_ICON}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3.5 8l3 3 6-6" stroke="#1B7A4B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={COMPLETE_TITLE}>Workflow Complete</span>
                  <span style={COMPLETE_SUB}>Completed in {totalElapsed} · {completedCount} steps executed</span>
                </div>
              </div>
              {/* Output summary */}
              {run.steps?.some((s) => s.output) && (
                <div style={OUTPUT_BOX}>
                  <span style={OUTPUT_LABEL}>Output Summary</span>
                  <span style={OUTPUT_TEXT}>
                    {orderedSteps.filter((s) => s.output).map((s) => typeof s.output === 'string' ? s.output : '').filter(Boolean).slice(-1)[0] ?? 'No output available.'}
                  </span>
                </div>
              )}
              {/* Actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <button type="button" onClick={onClose} style={VIEW_RESULTS_BTN}>View Results</button>
                <button type="button" style={OUTLINE_BTN}>Export</button>
                {onRunAgain && (
                  <button type="button" onClick={onRunAgain} style={OUTLINE_BTN}>Run Again</button>
                )}
              </div>
            </div>
          )}

          {/* ─── Failed Card ───────────────────────── */}
          {run && run.status === 'failed' && (
            <div style={{ ...COMPLETE_CARD, borderColor: '#D97645' }}>
              <div style={COMPLETE_HEADER}>
                <div style={{ ...COMPLETE_ICON, backgroundColor: 'rgba(196,98,42,0.12)' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4 4l8 8M12 4l-8 8" stroke="#C4622A" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span style={COMPLETE_TITLE}>Workflow Failed</span>
                  <span style={{ ...COMPLETE_SUB, color: '#A84518' }}>{run.error ?? 'An unknown error occurred.'}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {onGoBack && (
                  <button type="button" onClick={onGoBack} style={VIEW_RESULTS_BTN}>Go back</button>
                )}
                {onRunAgain && (
                  <button type="button" onClick={onRunAgain} style={onGoBack ? OUTLINE_BTN : VIEW_RESULTS_BTN}>Retry</button>
                )}
                <button type="button" onClick={onClose} style={OUTLINE_BTN}>Close</button>
              </div>
            </div>
          )}

          {orderedSteps.length === 0 && run && (
            <div style={EMPTY}><span>Waiting for first step…</span></div>
          )}
        </div>

        {/* ─── Run Details Sidebar ─────────────────── */}
        <div style={SIDEBAR}>
          {/* Section header */}
          <span style={SIDEBAR_HEADER}>Run Details</span>

          {/* Metadata */}
          <div style={META_SECTION}>
            <div style={META_ROW}>
              <span style={META_KEY}>Workflow</span>
              <span style={META_VAL}>{run?.definition?.label ?? '—'}</span>
            </div>
            <div style={META_ROW}>
              <span style={META_KEY}>Template</span>
              <div style={TEMPLATE_BADGE}><span style={TEMPLATE_TEXT}>Built-in</span></div>
            </div>
            <div style={META_ROW}>
              <span style={META_KEY}>Started</span>
              <span style={META_VAL}>{formatTime(run?.startedAt ?? null)}</span>
            </div>
            <div style={META_ROW}>
              <span style={META_KEY}>Elapsed</span>
              <span style={{ ...META_VAL, color: '#D97645', fontWeight: 600 }}>{totalElapsed}</span>
            </div>
            <div style={META_ROW}>
              <span style={META_KEY}>Steps</span>
              <span style={META_VAL}>{completedCount} of {orderedSteps.length}</span>
            </div>
          </div>

          {/* Input */}
          <div style={INPUT_SECTION}>
            <span style={SIDEBAR_HEADER}>Input</span>
            <div style={INPUT_FILE_CARD}>
              <div style={PDF_ICON}>
                <span style={{ fontSize: 8, fontWeight: 700, color: '#FFFFFF', letterSpacing: '0.02em' }}>PDF</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: APP.textPrimary, lineHeight: '16px' }}>
                  {run?.documentIds?.[0] ? 'Document' : '—'}
                </span>
                <span style={{ fontSize: 11, fontWeight: 400, color: APP.textSecondary, lineHeight: '14px' }}>
                  Uploaded
                </span>
              </div>
            </div>
          </div>

          {/* Output */}
          {run?.status === 'completed' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#1B7A4B', flexShrink: 0 }} />
                <span style={{ ...SIDEBAR_HEADER, color: '#D97645' }}>Output</span>
              </div>
              <div style={OUTPUT_SIDEBAR_BOX}>
                <span style={{ fontSize: 12, fontWeight: 400, color: APP.textCardDetail, lineHeight: '18px' }}>
                  {orderedSteps.filter((s) => s.output).map((s) => typeof s.output === 'string' ? s.output : '').filter(Boolean).slice(-1)[0] ?? 'No output.'}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────

function StepIcon({ status }: { status: WorkflowRunStep['status'] }) {
  if (status === 'completed') {
    return (
      <div style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: '#1B7A4B', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M2.5 6l2.5 2.5 4.5-4.5" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    );
  }
  if (status === 'failed') {
    return (
      <div style={{ width: 28, height: 28, borderRadius: 14, backgroundColor: '#C4622A', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 3l6 6M9 3l-6 6" stroke="#FFFFFF" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
    );
  }
  if (status === 'running') {
    return (
      <div style={{ width: 28, height: 28, borderRadius: 14, border: '2px solid #D97645', backgroundColor: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <div style={{ width: 10, height: 10, borderRadius: 5, border: '2px solid #D97645', borderTopColor: 'transparent', animation: 'spin 0.9s linear infinite' }} />
      </div>
    );
  }
  // pending / skipped
  return (
    <div style={{ width: 28, height: 28, borderRadius: 14, border: '1.5px solid #C5BAB0', backgroundColor: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: '#C5BAB0' }} />
    </div>
  );
}

// ─── Ordering ──────────────────────────────────────────────────────────────

function orderSteps(steps: WorkflowRunStep[], definition?: WorkflowDefinition): WorkflowRunStep[] {
  if (!definition) return steps;
  const order = new Map<string, number>();
  definition.graph.nodes.forEach((n, i) => order.set(n.nodeId, i));
  return [...steps].sort((a, b) => (order.get(a.nodeId) ?? 999) - (order.get(b.nodeId) ?? 999));
}

function stepStatusColor(status: WorkflowRunStep['status']): string {
  switch (status) {
    case 'completed': return '#1B7A4B';
    case 'failed': return '#C4622A';
    case 'running': return '#D97645';
    default: return APP.textQueueBody;
  }
}

function stepCardStyle(status: WorkflowRunStep['status'], nodeType?: string): CSSProperties {
  const borderColor =
    status === 'completed' && nodeType === 'document_input' ? '#C8E6C9' :
    status === 'running' ? '#D97645' :
    status === 'failed' ? '#D97645' :
    '#E8DED4';
  return {
    display: 'flex',
    width: 460,
    alignItems: 'flex-start',
    borderRadius: 10,
    paddingBlock: 16,
    paddingInline: 20,
    gap: 14,
    backgroundColor: APP.bgCardBody,
    border: `1px solid ${borderColor}`,
  };
}

// ─── Styles ────────────────────────────────────────────────────────────────

const CONTAINER: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 0%',
  backgroundColor: APP.bgContent,
  fontFamily: APP.fontFamily,
  minHeight: 0,
};

const TOP_BAR: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingBlock: 12,
  paddingInline: 24,
  position: 'relative',
  backgroundColor: APP.bgContent,
  borderBottom: `1px solid ${APP.border}`,
  flexShrink: 0,
};

const BREADCRUMB_BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: APP.fontFamily,
};

const BREADCRUMB_TEXT: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: APP.textSecondary,
  lineHeight: '16px',
};

const DIVIDER_V: CSSProperties = {
  width: 1,
  height: 20,
  backgroundColor: APP.border,
  flexShrink: 0,
};

const WORKFLOW_NAME_STYLE: CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: APP.textPrimary,
  lineHeight: '20px',
};

const STATUS_BADGE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  borderRadius: 12,
  paddingBlock: 4,
  paddingInline: 10,
  backgroundColor: '#FFF3E8',
  border: '1px solid #F0DFD0',
};

const STATUS_DOT: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: 3,
  flexShrink: 0,
};

const STATUS_BADGE_TEXT: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  flexShrink: 0,
};

const TOPBAR_BTN: CSSProperties = {
  borderRadius: 6,
  paddingBlock: 6,
  paddingInline: 14,
  border: `1px solid ${APP.border}`,
  background: 'transparent',
  color: APP.textSecondary,
  fontFamily: APP.fontFamily,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const PROGRESS_TRACK: CSSProperties = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: 3,
  backgroundColor: APP.border,
};

const PROGRESS_FILL: CSSProperties = {
  height: '100%',
  backgroundColor: '#D97645',
  borderRadius: '0 2px 2px 0',
  transition: 'width 0.3s ease',
};

// ─── Main Content ──────────────────────────────────────────────────────────

const MAIN_CONTENT: CSSProperties = {
  display: 'flex',
  flexGrow: 1,
  minHeight: 0,
};

const STEP_FLOW: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  alignItems: 'center',
  paddingBlock: 40,
  paddingInline: 40,
  overflow: 'auto',
  backgroundColor: APP.bgCodeBlock,
};

const EMPTY: CSSProperties = {
  padding: '48px 0',
  color: APP.textSecondary,
  fontSize: 13,
  fontFamily: APP.fontFamily,
};

// ─── Step Card ─────────────────────────────────────────────────────────────

const STEP_EYEBROW_STYLE: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  lineHeight: '14px',
};

const STEP_TIME: CSSProperties = {
  fontSize: 11,
  fontWeight: 400,
  color: APP.textQueueBody,
  lineHeight: '14px',
};

const STEP_TITLE: CSSProperties = {
  fontSize: 15,
  fontWeight: 600,
  color: APP.textPrimary,
  lineHeight: '18px',
};

const STEP_DESC: CSSProperties = {
  fontSize: 13,
  fontWeight: 400,
  color: APP.textCardDetail,
  lineHeight: '16px',
};

const STEP_ERR: CSSProperties = {
  fontSize: 12,
  fontWeight: 400,
  color: '#A84518',
  lineHeight: '16px',
  marginTop: 2,
};

// ─── Success / Complete Card ───────────────────────────────────────────────

const COMPLETE_CARD: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: 460,
  borderRadius: 12,
  paddingBlock: 24,
  paddingInline: 24,
  gap: 16,
  backgroundColor: APP.bgCodeBlock,
  border: '1px solid #1B7A4B',
};

const COMPLETE_HEADER: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
};

const COMPLETE_ICON: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: 16,
  backgroundColor: 'rgba(27,122,75,0.12)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const COMPLETE_TITLE: CSSProperties = {
  fontSize: 16,
  fontWeight: 600,
  color: APP.textCardBody,
  lineHeight: '20px',
};

const COMPLETE_SUB: CSSProperties = {
  fontSize: 13,
  fontWeight: 400,
  color: APP.textCardDetail,
  lineHeight: '16px',
};

const OUTPUT_BOX: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 8,
  paddingBlock: 14,
  paddingInline: 14,
  gap: 8,
  backgroundColor: APP.bgCodeBlock,
  border: `1px solid ${APP.border}`,
};

const OUTPUT_LABEL: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: APP.textCardDetail,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  lineHeight: '16px',
};

const OUTPUT_TEXT: CSSProperties = {
  fontSize: 14,
  fontWeight: 400,
  color: APP.textCardBody,
  lineHeight: '20px',
};

const VIEW_RESULTS_BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  paddingBlock: 10,
  paddingInline: 20,
  backgroundColor: '#D97645',
  border: 'none',
  color: '#FFFFFF',
  fontFamily: APP.fontFamily,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

const OUTLINE_BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  paddingBlock: 10,
  paddingInline: 16,
  backgroundColor: 'transparent',
  border: `1px solid ${APP.border}`,
  color: APP.textPrimary,
  fontFamily: APP.fontFamily,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
};

// ─── Sidebar ───────────────────────────────────────────────────────────────

const SIDEBAR: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: 340,
  flexShrink: 0,
  paddingBlock: 24,
  paddingInline: 24,
  gap: 20,
  backgroundColor: APP.bgContent,
  borderLeft: `1px solid ${APP.border}`,
  overflowY: 'auto',
};

const SIDEBAR_HEADER: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: APP.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  lineHeight: '14px',
};

const META_SECTION: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: 16,
  gap: 10,
  borderBottom: `1px solid ${APP.border}`,
};

const META_ROW: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
};

const META_KEY: CSSProperties = {
  fontSize: 13,
  fontWeight: 400,
  color: APP.textSecondary,
  lineHeight: '16px',
};

const META_VAL: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: APP.textPrimary,
  lineHeight: '16px',
};

const TEMPLATE_BADGE: CSSProperties = {
  display: 'flex',
  borderRadius: 4,
  paddingBlock: 2,
  paddingInline: 8,
  backgroundColor: APP.bgCodeBlock,
};

const TEMPLATE_TEXT: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: APP.textCardDetail,
  lineHeight: '14px',
};

const INPUT_SECTION: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: 16,
  gap: 10,
  borderBottom: `1px solid ${APP.border}`,
};

const INPUT_FILE_CARD: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  borderRadius: 8,
  paddingBlock: 10,
  paddingInline: 12,
  gap: 8,
  backgroundColor: APP.bgCodeBlock,
};

const PDF_ICON: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 18,
  flexShrink: 0,
  borderRadius: 3,
  backgroundColor: '#E74C3C',
};

const OUTPUT_SIDEBAR_BOX: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 8,
  paddingBlock: 12,
  paddingInline: 12,
  gap: 8,
  backgroundColor: APP.bgCodeBlock,
};
